import { MouseEvent, useState, useMemo, useCallback } from 'react'
import { useLocaleState, useLocales } from 'ra-core'
import { Box, Button, Menu, MenuItem, styled, Typography } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { LocalesMenuButtonProps, LanguageProps } from './type'

const PREFIX = 'RaLocalesMenuButton'

export const LocalesMenuButtonClasses = {
  selectedLanguage: `${PREFIX}-selectedLanguage`,
}

const Root = styled(Box, {
  name: PREFIX,
  overridesResolver: (props, styles) => styles.root,
})(({ theme }) => ({
  [`& .${LocalesMenuButtonClasses.selectedLanguage}`]: {
    marginLeft: theme.spacing(1),
  },
}))

export const LocalesMenuButton: React.FC<LocalesMenuButtonProps> = (
  props: LocalesMenuButtonProps,
) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const languages = useLocales({ locales: props.languages })
  const [locale, setLocale] = useLocaleState()

  const getNameForLocale = useCallback(
    (locale: string): LanguageProps | null => {
      const language = languages.find((language) => language.locale === locale)
      return language ? language : null
    },
    [languages],
  )

  const activeLocale = useMemo(() => getNameForLocale(locale), [getNameForLocale, locale])

  const changeLocale = (locale: string) => (): void => {
    setLocale(locale)
    setAnchorEl(null)
  }

  const handleLanguageClick = (event: MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = (): void => {
    setAnchorEl(null)
  }

  return (
    <Root>
      <Button
        color='inherit'
        aria-controls='simple-menu'
        aria-label=''
        aria-haspopup='true'
        onClick={handleLanguageClick}
      >
        <img src={activeLocale.icon} style={{ width: 28 }} />
        <ExpandMoreIcon fontSize='small' />
      </Button>
      <Menu
        id='simple-menu'
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {languages.map((language: LanguageProps) => (
          <MenuItem
            key={language.locale}
            onClick={changeLocale(language.locale)}
            selected={language.locale === locale}
          >
            <img src={language.icon} style={{ width: 28, marginRight: 16 }} />
            <Typography
              component='div'
              color='text.primary'
              className={LocalesMenuButtonClasses.selectedLanguage}
            >
              {language.name}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </Root>
  )
}
